import {
  CustomerCopies,
  CustomerPropertiesMap,
  EligibilityType,
  generateCustomerCopyWithProperties,
} from '@lyrahealth-inc/shared-app-logic'

import { customers } from '../../customerConstants'

/**
 * This function is called in two scenarios, initially when the user has not signed in yet
 * and again after the user has signed in and we have their eligibility type.
 *
 * The eligibility type determines specific copies from showing and won't exist if the user
 * has not signed in yet
 */
export const getCustomerCopy = (
  initialCustomerName?: string,
  customerPropertiesMap?: CustomerPropertiesMap,
  eligibilityType?: EligibilityType,
  customerDisplayName: string = '',
  isCustomerDynamicSessionLimitEnabled: boolean = false,
  isLyraForLyrians2026Enabled: boolean = false,
): CustomerCopies => {
  const customerName = initialCustomerName ?? 'defaultCustomer'
  const customerCopy = _getCustomerCopy(
    customerName,
    customerDisplayName,
    isCustomerDynamicSessionLimitEnabled,
    isLyraForLyrians2026Enabled,
    customerPropertiesMap,
    eligibilityType,
  )
  _extendCustomerCopyWithFeatureVariations(
    customerName,
    customerDisplayName,
    isCustomerDynamicSessionLimitEnabled,
    customerCopy,
  )

  return customerCopy
}

const _getCustomerCopy = (
  customerName: string,
  customerDisplayName: string,
  isCustomerDynamicSessionLimitEnabled: boolean,
  isLyraForLyrians2026Enabled: boolean,
  customerPropertiesMap?: CustomerPropertiesMap,
  eligibilityType?: EligibilityType,
): CustomerCopies => {
  const customer = customers[customerName] ?? { copy: {}, template: {} }
  const customerInfo = customerPropertiesMap ? customerPropertiesMap : {}
  const customerInfoPlusEligibility = { ...customerInfo, eligibilityType }
  const customerSpecificCopy = generateCustomerCopyWithProperties({
    customerName,
    copy: customer?.copy ? customer.copy : {},
    customerInfo: customerInfoPlusEligibility,
    customerDisplayName: customerDisplayName,
    isCustomerDynamicSessionLimitEnabled,
    isLyraForLyrians2026Enabled,
  })
  const templateWithCustomerSpecificCopy = generateCustomerCopyWithProperties({
    customerName,
    copy: customer?.template ? customer.template : {},
    customerInfo,
    customerDisplayName,
    isCustomerDynamicSessionLimitEnabled,
    isLyraForLyrians2026Enabled,
  })
  const defaultCustomerCopy = generateCustomerCopyWithProperties({
    customerName,
    copy: customers.defaultCustomer.copy ? customers.defaultCustomer.copy : {},
    customerInfo,
    customerDisplayName,
    isCustomerDynamicSessionLimitEnabled,
    isLyraForLyrians2026Enabled,
  })

  const customerCopy: CustomerCopies = {
    ...defaultCustomerCopy,
    ...templateWithCustomerSpecificCopy,
    ...customerSpecificCopy,
  }

  return customerCopy
}

const _extendCustomerCopyWithFeatureVariations = (
  customerName: string,
  customerDisplayName: string,
  isCustomerDynamicSessionLimitEnabled: boolean,
  customerCopy: CustomerCopies,
): CustomerCopies => {
  const variations = {}
  // Add copies for feature variations, for example:
  // if (customerName === CustomerName.WALMART) {
  //   variations = {
  //     isWalmartHardEligibilityCheckEnabled: _getCustomerCopy('walmart_isWalmartHardEligibilityCheckEnabled'),
  //   }
  // }
  customerCopy._FEATURE_VARIATIONS = variations

  return customerCopy
}
